// src/lib/crop/computeCoverCropWrapperStyle.ts
import type { CSSProperties } from 'react';

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

/**
 * Style for the cell that hosts a cover-crop wrapper. The wrapper is sized in
 * container query units (cqw/cqh), which resolve against the nearest size
 * container — without this style on its parent they silently fall back to the
 * viewport. Fills the positioned cell it is placed in.
 */
export const COVER_CROP_CONTAINER_STYLE: CSSProperties = {
  position: 'absolute',
  inset: 0,
  overflow: 'hidden',
  containerType: 'size',
};

/**
 * Style for an oversized wrapper div that crops media to its cell for media
 * that cannot be cropped with CSS object-* properties — the @remotion/media
 * <Video> draws into a canvas and ignores them (it only supports a plain
 * `objectFit` prop, with no focal point).
 *
 * Same rule as the "Reenquadrar vídeo" dialog box, the default composition and
 * the cover capture: the crop point (x%, y%) of the source sits at the CENTER
 * of the cell, and the window is clamped so the media always covers the cell.
 * Zoom shrinks the window around that point.
 *
 * Geometry, in container units so the cell's pixel size is never needed:
 * - width = cover width × zoom = max(100cqw, 100cqh × sourceAspect) × zoom,
 *   height the same divided by the aspect, so the media is never stretched.
 * - left = clamp(cellWidth − width, cellWidth/2 − width × x, 0), top likewise.
 *
 * Render it as the only child of a COVER_CROP_CONTAINER_STYLE cell; the media
 * inside must simply fill it (100% × 100%).
 */
export function computeCoverCropWrapperStyle({
  cropPosition,
  sourceAspect,
}: {
  cropPosition: { x: number; y: number; zoom: number };
  sourceAspect: number;
}): CSSProperties {
  const x = clamp(cropPosition.x, 0, 100) / 100;
  const y = clamp(cropPosition.y, 0, 100) / 100;
  const zoom = Math.max(1, cropPosition.zoom);
  const width = `calc(max(100cqw, 100cqh * ${sourceAspect}) * ${zoom})`;
  const height = `calc(max(100cqw / ${sourceAspect}, 100cqh) * ${zoom})`;

  return {
    position: 'absolute',
    width,
    height,
    left: `clamp(calc(100cqw - ${width}), calc(50cqw - ${width} * ${x}), 0px)`,
    top: `clamp(calc(100cqh - ${height}), calc(50cqh - ${height} * ${y}), 0px)`,
  };
}
